import React, { useCallback } from 'react';
import {
    Dropdown,
    IDropdownOption,
    ISelectableOption,
    IStackTokens,
    Icon,
    IconButton,
    Link,
    Panel,
    PanelType,
    Stack,
    Text,
    Pivot,
    PivotItem,
} from '@fluentui/react';
import { useContext, useEffect, useState } from 'react';
import {
    getDependencyDisplayName,
    TimelineItem,
} from '../../../models/Timeline';
import { Guid } from '../../../../Common/models/Guid';
import { Deployment } from '../../../models/Deployment';
import { getCurrentReplicationTimeline } from '../../../util/ReleaseUtils';
import { ThemeContext } from '../../../../HubLayout/models/ThemeContext';
import { getThemeFromString } from '../../../../Common/util/localStorageUtils';
import { TimelineChart } from './Status/TimelineChart';
import { CompletionIndicatorIcon } from './Status/CompletionIndicatorIcon';
import { DetailsPanelTextBox } from './DetailsPanelTextBox';
import { Build } from '../../../models/Build';
import { Replication } from '../../../models/Replication';
import { Capability } from '../../../../Common/components/Capabilities/capability';
import { CapabilityContext } from '../../../../Common/components/Capabilities/CapabilityContext';
import { useIsMobile } from '../../../../Common/hooks/useIsMobile';

import { DefaultHubButton } from '../../../../HubLayout/components/Utilities/DefaultHubButton';
import { usePromise } from '../../../../Common/hooks/usePromise';
import { JsonModal } from '../../../../HubLayout/components/JsonModal';
import { AgrmType, getAgrmData } from '../../../api/AGRMApi';
import {
    getConfig,
    GlobalConfigProperties,
} from '../../../../Common/api/ABHub/ABHubGlobalConfig';
import { isValidCloudOrEnvironment } from '../../../api/AGRMApi';

import { CancelToken } from 'axios';
import { CurrentReplicationTimeline } from './Status/CurrentReplicationTimeline';
import { ReplicationStatusResult } from '../../../models/ReplicationStatusResult';

interface IDetailsPanelProps {
    isOpen: boolean;
    dismissPanel:
        | ((
              ev?:
                  | React.SyntheticEvent<HTMLElement, Event>
                  | KeyboardEvent
                  | undefined
          ) => void)
        | undefined;
    selected: Deployment | Build | undefined;
    availableItems?: Array<Deployment | Build>;
    isLoaded: boolean;
    serviceId?: Guid;
    showBackNavigation?: boolean;
    onDisplayedItemChange?: (item?: Deployment | Build) => void;
    onDependencyNavigate?: (
        sourceIdentifier: string,
        destinationIdentifier: string,
        destinationEntity?: Deployment | Build
    ) => void;
    resolveDependencyItem?: (
        sourceItem: Deployment | Build,
        dependencyIdentifier: string,
        dependencyType: 'Build' | 'Deployment'
    ) => Deployment | Build | undefined;
    resolveItemStageName?: (item: Deployment | Build) => string | undefined;
}

export const DetailsPanel: React.FC<IDetailsPanelProps> = (
    props: IDetailsPanelProps
) => {
    const capabilities = useContext(CapabilityContext);

    const themeContext = useContext(ThemeContext);
    const theme = getThemeFromString(themeContext.themeName);

    const [selected, setSelected] = useState<Deployment | Build>();
    const [nestedSelected, setNestedSelected] = useState<Deployment | Build>();
    const [selectedReplication, setSelectedReplication] =
        useState<Replication>();
    const [currentFlightTimeline, setCurrentFlightTimeline] =
        useState<TimelineItem[]>();
    const [options, setOptions] = useState<IDropdownOption[]>([]);
    const [show, setShow] = useState<Boolean>(false);
    const [lastSelectedOption, setLastSelectedOption] =
        useState<IDropdownOption>();
    const isMobile = useIsMobile();

    const [openModal, setOpenModal] = useState(false);
    const [jsonModalData, setJsonModalData] = useState<string>('');
    const [jsonModalHeader, setJsonModalHeader] = useState<string>('');
    const [error, setError] = useState<string>('');

    const [sreApiIsAvailable, setSreApiIsAvailable] = useState<boolean>(false);

    // Tokens definition
    const stackTokens: IStackTokens = { childrenGap: 10 };

    // JavaScript/TypeScript's weird way of type checking
    const isDeployment = (selectedItem: Replication | undefined) => {
        // Have to use a unique property that deployment has in order to check if it is a deployment or not
        return selectedItem?.replicationType === 'Deployment';
    };

    const onRenderOption = (
        props: ISelectableOption<any> | undefined
    ): JSX.Element => {
        return (
            <div
                style={{
                    display: 'flex',
                }}
            >
                {props?.data && props.data.icon && (
                    <CompletionIndicatorIcon
                        theme={theme}
                        completionIndicator={props.data.icon}
                        completionIndicatorString={props.data.icon}
                    />
                )}
                <span
                    style={{
                        paddingLeft: '8px',
                    }}
                >
                    {props?.text}
                </span>
            </div>
        );
    };

    const onRenderTitle = (
        props: ISelectableOption<any>[] | undefined
    ): JSX.Element => {
        var option = undefined;

        if (props) {
            option = props[0];
        }

        return (
            <div style={{ display: 'flex' }}>
                {option?.data && option.data.icon && (
                    <CompletionIndicatorIcon
                        theme={theme}
                        completionIndicator={option.data.icon}
                        completionIndicatorString={option.data.icon}
                    />
                )}
                <span
                    style={{
                        paddingLeft: '8px',
                    }}
                >
                    {option?.text}
                </span>
            </div>
        );
    };

    const environment = getConfig(GlobalConfigProperties.Environment);
    const cloud = getConfig(GlobalConfigProperties.Cloud);

    useEffect(() => {
        if (isValidCloudOrEnvironment(cloud, environment)) {
            setSreApiIsAvailable(true);
        } else {
            console.log('SRE API is not available', cloud, environment);
            setSreApiIsAvailable(false);
        }
    }, [cloud, environment]);

    // When parent component sends new data, refresh my state accordingly
    useEffect(() => {
        if (props.selected) {
            setSelected(props.selected);
            setNestedSelected(undefined);
            props.onDisplayedItemChange?.(props.selected);
            setCurrentFlightTimeline(
                getCurrentReplicationTimeline(props.selected.currentReplication)
            );
            setSelectedReplication(props.selected.currentReplication);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.selected]);

    useEffect(() => {
        const opts: IDropdownOption[] = [];
        // Order replications by latest
        if (selected) {
            const repList = selected.replications
                ?.filter((rep) => Boolean(rep))
                .slice()
                .sort((r1, r2) => {
                    if (r1.createdOn && r2.createdOn) {
                        return (
                            Date.parse(r2.createdOn) - Date.parse(r1.createdOn)
                        );
                    }
                    return 0;
                });

            repList?.forEach((rep, index) => {
                const opt: IDropdownOption = {
                    key: `attempt${repList.length - index}`,
                    text: `Attempt #${repList.length - index}`,
                    data: {
                        icon: rep.completionIndicator,
                        replication: rep,
                    },
                };
                opts.push(opt);
            });

            setOptions(opts);

            setLastSelectedOption(opts[0]);

            if (opts.length > 0) {
                setCurrentFlightTimeline(
                    getCurrentReplicationTimeline(opts[0].data.replication)
                );
                setSelectedReplication(opts[0].data.replication);
            }

            setShow(true);
        }
    }, [selected]);

    const onSelectAttempt = (
        event: React.FormEvent<HTMLDivElement>,
        option?: IDropdownOption,
        index?: number
    ) => {
        if (option && option.data) {
            setCurrentFlightTimeline(
                getCurrentReplicationTimeline(option.data.replication)
            );
            setSelectedReplication(option.data.replication);
            setLastSelectedOption(option);
            setShow(true);
        }
    };

    const selectedDeployment =
        selected && 'deploymentType' in selected
            ? (selected as Deployment)
            : undefined;

    const resolveDependency = (
        uniqueIdentifier: string,
        dependencyType: 'Build' | 'Deployment'
    ) => {
        const item = selected?.uniqueIdentifier
            ? props.resolveDependencyItem?.(
                  selected,
                  uniqueIdentifier,
                  dependencyType
              )
            : props.availableItems?.find(
                  (candidate) => candidate.uniqueIdentifier === uniqueIdentifier
              );

        // A build's status is per environment, so it lives on the current replication
        // rather than on the build itself - the same source the build list renders.
        const buildReplication =
            dependencyType === 'Build'
                ? (item as Build | undefined)?.currentReplication
                : undefined;

        return {
            uniqueIdentifier,
            displayName: getDependencyDisplayName(
                dependencyType,
                uniqueIdentifier,
                (item as Deployment | undefined)?.displayNameParsed,
                item ? props.resolveItemStageName?.(item) : undefined
            ),
            dependencyType,
            completionIndicator:
                buildReplication?.completionIndicator ??
                item?.completionIndicator ??
                'NotStarted',
            completionIndicatorString:
                buildReplication?.completionIndicatorString ??
                item?.completionIndicatorString ??
                'Not available',
            isAvailable: item !== undefined,
        };
    };

    const dependencies = selectedDeployment
        ? [
              ...(selectedDeployment.dependentBuildUniqueIdentifiers ?? []).map(
                  (identifier) => resolveDependency(identifier, 'Build')
              ),
              ...(
                  selectedDeployment.dependentDeploymentUniqueIdentifiers ?? []
              ).map((identifier) =>
                  resolveDependency(identifier, 'Deployment')
              ),
          ]
        : [];

    const openDependency = (uniqueIdentifier: string) => {
        const dependencyType =
            selectedDeployment?.dependentBuildUniqueIdentifiers?.includes(
                uniqueIdentifier
            )
                ? 'Build'
                : 'Deployment';
        const dependency = selected?.uniqueIdentifier
            ? props.resolveDependencyItem?.(
                  selected,
                  uniqueIdentifier,
                  dependencyType
              )
            : props.availableItems?.find(
                  (item) => item.uniqueIdentifier === uniqueIdentifier
              );
        if (dependency) {
            setNestedSelected(dependency);
            props.onDisplayedItemChange?.(dependency);
            if (selected?.uniqueIdentifier && dependency.uniqueIdentifier) {
                props.onDependencyNavigate?.(
                    selected.uniqueIdentifier,
                    dependency.uniqueIdentifier,
                    dependency
                );
            }
        }
    };

    const dismissNestedPanel = () => {
        const sourceIdentifier = nestedSelected?.uniqueIdentifier;
        const destinationIdentifier = selected?.uniqueIdentifier;
        setNestedSelected(undefined);
        props.onDisplayedItemChange?.(selected);
        if (sourceIdentifier && destinationIdentifier) {
            window.requestAnimationFrame(() =>
                props.onDependencyNavigate?.(
                    sourceIdentifier,
                    destinationIdentifier
                )
            );
        }
    };

    const getHeaderText = () => {
        var headerText = isDeployment(selectedReplication)
            ? (selected as Deployment)?.displayNameParsed ?? 'Deployment'
            : (selected as Build)?.buildNumber ?? 'Build';

        return `${headerText} Details`;
    };

    const headerText = getHeaderText();
    const renderNavigation = props.showBackNavigation
        ? () => (
              <Stack
                  horizontal
                  verticalAlign="center"
                  styles={{
                      root: {
                          minHeight: 44,
                          padding: '0 12px 0 24px',
                          width: '100%',
                      },
                  }}
                  tokens={{ childrenGap: 8 }}
              >
                  <Stack.Item grow styles={{ root: { minWidth: 0 } }}>
                      <Text
                          block
                          nowrap
                          variant="xLarge"
                          styles={{ root: { fontWeight: 600 } }}
                      >
                          {headerText}
                      </Text>
                  </Stack.Item>
                  <Link onClick={props.dismissPanel}>{'← Go back'}</Link>
                  <IconButton
                      iconProps={{ iconName: 'Cancel' }}
                      ariaLabel="Close"
                      title="Close"
                      onClick={() => props.dismissPanel?.()}
                  />
              </Stack>
          )
        : undefined;

    const hideModal = () => {
        setOpenModal(false);
        setJsonModalData('');
        setError('');
    };

    const openJsonModal = (header: string, stateFunction: Function) => {
        setOpenModal(true);
        setJsonModalHeader(header);
        stateFunction();
    };

    const openJsonHistoryModal = (header: string) => {
        const stringified = JSON.stringify(
            selectedReplication?.statusListHistory,
            null,
            2
        );
        setJsonModalData(stringified);
        setOpenModal(true);
        setJsonModalHeader(header);
    };

    const getAgrmApiCall = async (
        apiCall: Function,
        cancelToken?: CancelToken
    ) => {
        var agrmType = isDeployment(selectedReplication)
            ? AgrmType.Deployment
            : AgrmType.Build;
        var uid =
            selectedReplication?.uniqueIdentifier ?? selected?.uniqueIdentifier; // fallback to selected.uniqueIdentifier for legacy replications
        if (uid) {
            const result = await apiCall(uid, agrmType, cancelToken);
            const stringified = JSON.stringify(result, null, 2);
            setJsonModalData(stringified);
        } else {
            throw new Error(
                'Failed to get Agrm Information. Invalid Unique Identifier.'
            );
        }
    };

    const setSreJsonData = useCallback(
        () => getAgrmApiCall(getAgrmData),
        [selectedReplication?.id]
    );

    const [
        ,
        sreApiRequestError,
        sreApiRequestDataIsLoaded,
        sreApiRequestStart,
    ] = usePromise(setSreJsonData);

    useEffect(() => {
        if (sreApiRequestError) {
            setError(sreApiRequestError.message);
        }
    }, [sreApiRequestError]);

    return (
        <div className="DetailsPanel-root">
            <Panel
                isBlocking={!nestedSelected}
                isLightDismiss={!nestedSelected}
                isOpen={props.isOpen}
                onDismiss={props.dismissPanel}
                onRenderNavigation={renderNavigation}
                closeButtonAriaLabel="Close"
                headerText={props.showBackNavigation ? undefined : headerText}
                aria-label={props.showBackNavigation ? headerText : undefined}
                type={PanelType.custom}
                customWidth={!isMobile ? '800px' : undefined}
            >
                <Stack tokens={stackTokens}>
                    {selected && selected.completionIndicator && (
                        <Stack>
                            <Text
                                variant={'mediumPlus'}
                                style={{
                                    paddingTop: '5px',
                                    paddingBottom: '5px',
                                }}
                            >
                                Attempts
                            </Text>

                            <Dropdown
                                placeholder="Attempts"
                                onRenderOption={onRenderOption}
                                onRenderTitle={onRenderTitle}
                                onChange={onSelectAttempt}
                                defaultSelectedKey={
                                    lastSelectedOption
                                        ? lastSelectedOption.key
                                        : options.length > 0
                                        ? options[0].key
                                        : ''
                                }
                                options={options}
                            />
                        </Stack>
                    )}
                    {selected && selected.currentReplication ? (
                        <Stack tokens={stackTokens}>
                            <Pivot aria-label="Deployment Details">
                                <PivotItem
                                    headerText="Timeline"
                                    itemIcon="Timeline"
                                >
                                    <Stack
                                        tokens={stackTokens}
                                        style={{
                                            paddingTop: '0.5em',
                                        }}
                                    >
                                        {show && (
                                            <TimelineChart
                                                currentFlightTimeline={
                                                    currentFlightTimeline
                                                }
                                            />
                                        )}
                                    </Stack>
                                    <Stack>
                                        {selectedReplication?.highSideReset && (
                                            <Stack
                                                horizontal
                                                style={{
                                                    backgroundColor:
                                                        selectedReplication?.highSideReset
                                                            ? theme.palette
                                                                  .blueLight
                                                            : undefined,
                                                    borderRadius:
                                                        selectedReplication?.highSideReset
                                                            ? '4px'
                                                            : undefined,
                                                    padding:
                                                        selectedReplication?.highSideReset
                                                            ? '8px'
                                                            : undefined,
                                                    marginTop:
                                                        selectedReplication?.highSideReset
                                                            ? '16px'
                                                            : undefined,
                                                }}
                                            >
                                                <Icon
                                                    iconName={'Warning'}
                                                    style={{
                                                        paddingTop: '5px',
                                                        paddingRight: '5px',
                                                        fontStyle: 'italic',
                                                    }}
                                                />
                                                <Text
                                                    variant="mediumPlus"
                                                    style={{
                                                        fontStyle: 'italic',
                                                    }}
                                                >
                                                    <strong>{`Redeployed from SRM Portal in ${selectedReplication.environmentId}`}</strong>

                                                    {selectedReplication.highSideResetSubmittedBy && (
                                                        <Text>
                                                            {` by ${selectedReplication.highSideResetSubmittedBy}`}
                                                        </Text>
                                                    )}
                                                </Text>
                                            </Stack>
                                        )}
                                        <CurrentReplicationTimeline
                                            currentFlightTimeline={
                                                currentFlightTimeline
                                            }
                                            currentCloudName={
                                                selectedReplication?.environmentId
                                            }
                                            dependencies={dependencies}
                                            onDependencyClick={openDependency}
                                        />
                                    </Stack>
                                </PivotItem>

                                <PivotItem headerText="Debug" itemIcon="Info">
                                    <Stack
                                        tokens={stackTokens}
                                        style={{
                                            paddingTop: '0.5em',
                                            paddingBottom: '20px',
                                        }}
                                    >
                                        <DetailsPanelTextBox
                                            title="Type"
                                            data={
                                                isDeployment(
                                                    selectedReplication
                                                )
                                                    ? (selected as Deployment)
                                                          .deploymentType
                                                    : (selected as Build)
                                                          .buildType
                                            }
                                        />

                                        <DetailsPanelTextBox
                                            title="Environment"
                                            data={
                                                selectedReplication?.environmentId
                                            }
                                        />

                                        <DetailsPanelTextBox
                                            title="Source"
                                            data={
                                                selectedReplication?.sourceType
                                            }
                                        />

                                        <DetailsPanelTextBox
                                            title="ID"
                                            data={
                                                isDeployment(
                                                    selectedReplication
                                                )
                                                    ? selectedReplication?.deploymentGuid
                                                    : selectedReplication?.buildInstanceCorrelationId
                                            }
                                            tooltip={
                                                isDeployment(
                                                    selectedReplication
                                                )
                                                    ? 'Deployment Guid'
                                                    : 'Build Instance Correlation ID'
                                            }
                                        />

                                        <DetailsPanelTextBox
                                            title="Correlation ID"
                                            data={
                                                selectedReplication?.correlationId
                                            }
                                        />

                                        {capabilities.check(
                                            Capability.srmAdminRole
                                        ) && (
                                            <DetailsPanelTextBox
                                                title="Kusto"
                                                data={
                                                    'GetRawUnprocessedEgressEvents("' +
                                                    selectedReplication?.correlationId +
                                                    '")'
                                                }
                                                tooltip={
                                                    'Compare egress events received with the timeline below.'
                                                }
                                            />
                                        )}

                                        <DetailsPanelTextBox
                                            title="Unique Identifier"
                                            data={
                                                selectedReplication?.uniqueIdentifier ??
                                                selected.uniqueIdentifier
                                            } // fallback to selected.uniqueIdentifier for legacy replications
                                        />

                                        {capabilities.check(
                                            Capability.srmAdminRole
                                        ) &&
                                            !isDeployment(
                                                selectedReplication
                                            ) && (
                                                <DetailsPanelTextBox
                                                    title="Caching Enabled"
                                                    data={selectedReplication?.buildCachingEnabled?.toString()}
                                                />
                                            )}

                                        {capabilities.check(
                                            Capability.srmAdminRole
                                        ) &&
                                            !isDeployment(
                                                selectedReplication
                                            ) && (
                                                <DetailsPanelTextBox
                                                    title="Storage Upload"
                                                    data={selectedReplication?.buildStorageUploadEnabled?.toString()}
                                                />
                                            )}

                                        {capabilities.check(
                                            Capability.srmAdminRole
                                        ) &&
                                            !isDeployment(
                                                selectedReplication
                                            ) && (
                                                <DetailsPanelTextBox
                                                    title="Required On Share"
                                                    data={selectedReplication?.buildRequiredOnNetworkShare?.toString()}
                                                />
                                            )}

                                        {isDeployment(selectedReplication) && (
                                            <DetailsPanelTextBox
                                                title="Lockbox Enabled"
                                                data={selectedReplication?.deploymentLockboxEnabled?.toString()}
                                            />
                                        )}
                                    </Stack>
                                    <Stack>
                                        {sreApiIsAvailable && (
                                            <Stack>
                                                <Stack horizontal>
                                                    <Text
                                                        variant={'mediumPlus'}
                                                    >
                                                        Processing Log{' '}
                                                    </Text>

                                                    {isDeployment(
                                                        selectedReplication
                                                    ) &&
                                                        selected?.replications
                                                            .length > 1 && (
                                                            <Text
                                                                variant={
                                                                    'mediumPlus'
                                                                }
                                                                style={{
                                                                    paddingLeft: 5,
                                                                }}
                                                            >
                                                                (
                                                                <strong>
                                                                    {
                                                                        lastSelectedOption?.text
                                                                    }
                                                                </strong>
                                                                )
                                                            </Text>
                                                        )}
                                                </Stack>
                                                <Stack
                                                    style={{ paddingTop: 10 }}
                                                >
                                                    <DefaultHubButton
                                                        buttonText="Show JSON"
                                                        icon="js"
                                                        onClick={() =>
                                                            openJsonModal(
                                                                'Processing Log',
                                                                sreApiRequestStart
                                                            )
                                                        }
                                                    />
                                                    <JsonModal
                                                        jsonData={
                                                            error !== ''
                                                                ? error
                                                                : jsonModalData ??
                                                                  ''
                                                        }
                                                        header={
                                                            jsonModalHeader ??
                                                            ''
                                                        }
                                                        isOpen={openModal}
                                                        closeModal={hideModal}
                                                        dataLoaded={
                                                            sreApiRequestDataIsLoaded ||
                                                            error !== ''
                                                        }
                                                    />
                                                </Stack>
                                            </Stack>
                                        )}

                                        {capabilities.check(
                                            Capability.srmAdminRole
                                        ) && (
                                            <Stack>
                                                <Stack horizontal>
                                                    <Text
                                                        variant={'mediumPlus'}
                                                    >
                                                        Egress History{' '}
                                                    </Text>
                                                </Stack>
                                                <Stack
                                                    style={{ paddingTop: 10 }}
                                                >
                                                    <DefaultHubButton
                                                        buttonText="Show JSON"
                                                        icon="js"
                                                        onClick={() =>
                                                            openJsonHistoryModal(
                                                                'Egress History'
                                                            )
                                                        }
                                                    />
                                                    <JsonModal
                                                        jsonData={
                                                            error !== ''
                                                                ? error
                                                                : jsonModalData ??
                                                                  ''
                                                        }
                                                        header={
                                                            jsonModalHeader ??
                                                            ''
                                                        }
                                                        isOpen={openModal}
                                                        closeModal={hideModal}
                                                        dataLoaded={true}
                                                    />
                                                </Stack>
                                            </Stack>
                                        )}
                                    </Stack>
                                </PivotItem>
                            </Pivot>
                        </Stack>
                    ) : (
                        <></>
                    )}
                </Stack>
            </Panel>
            {nestedSelected && (
                <DetailsPanel
                    isOpen
                    dismissPanel={dismissNestedPanel}
                    selected={nestedSelected}
                    availableItems={props.availableItems}
                    onDisplayedItemChange={props.onDisplayedItemChange}
                    onDependencyNavigate={props.onDependencyNavigate}
                    resolveDependencyItem={props.resolveDependencyItem}
                    resolveItemStageName={props.resolveItemStageName}
                    isLoaded={props.isLoaded}
                    serviceId={props.serviceId}
                    showBackNavigation
                />
            )}
        </div>
    );
};
